import {
	HttpClient as NgHttpClient,
	HttpErrorResponse,
	HttpHeaders,
	HttpParams,
	HttpRequest,
	HttpResponse,
} from "@angular/common/http";
import { Inject, Optional } from "@angular/core";
import { isExternalUrl } from "@obg/global.evo";
import { Log, LoggingFactory } from "@obg/ngx.global.evo";
import { forEach, isArray, isDate, isEmpty, isNil, isObjectLike, isString, isUndefined, merge, some } from "lodash-es";
import { noop, Observable, of, throwError, timeout, TimeoutError } from "rxjs";
import { catchError, map, tap } from "rxjs/operators";

import { REGISTER_KEY_DEPENDENCY_TOKEN, RegisterKeyDependencyFn } from "../model";
import { PrefetchResponse } from "../prefetch/prefetch.model";
import { PrefetchService } from "../prefetch/prefetch.service";
import { StatusCodes } from "../utils/http-status-codes";

import { SITE_CORE_COOKIE_NOT_IN_HEADER } from "./http-client.const";
import { ApiErrorResponse, HttpClient, HttpRequestMethod, RequestPayload } from "./http-client.model";

declare global {
	// eslint-disable-next-line no-var
	var OBG_DOM_SSR_MAXIMUM_TIMEOUT: number | undefined;
}

/**
 * WARNING: inform fe-evolution team if making any changes
 *
 * There is new microfrontend-compatible `GenericHttpClientService` in `ngx.global.evo` that needs to be kept in sync.
 * This client will be slowly phased out in favor of new client from `ngx.global.evo`
 */
export class HttpClientService implements HttpClient {
	private logger: Log;

	constructor(
		loggingFactory: LoggingFactory,
		private http: NgHttpClient,
		private prefetchService: PrefetchService,
		@Inject(REGISTER_KEY_DEPENDENCY_TOKEN) @Optional() private registerKeyDependency: RegisterKeyDependencyFn,
		private defaultConfiguration?: RequestPayload
	) {
		this.logger = loggingFactory.get("httpClientService");
	}

	get<T>(payload: RequestPayload): Observable<T> {
		return this.request<T>(HttpRequestMethod.Get, payload);
	}

	getRaw(payload: RequestPayload): Observable<string> {
		return this.request<string>(HttpRequestMethod.Get, payload, false);
	}

	post<T>(payload: RequestPayload): Observable<T> {
		return this.request<T>(HttpRequestMethod.Post, payload);
	}

	put<T>(payload: RequestPayload): Observable<T> {
		return this.request<T>(HttpRequestMethod.Put, payload);
	}

	delete<T>(payload: RequestPayload): Observable<T> {
		return this.request<T>(HttpRequestMethod.Delete, payload);
	}

	patch<T>(payload: RequestPayload): Observable<T> {
		return this.request<T>(HttpRequestMethod.Patch, payload);
	}

	head<T>(payload: RequestPayload): Observable<T> {
		return this.request<T>(HttpRequestMethod.Head, payload);
	}

	private request<T>(method: HttpRequestMethod, payload: RequestPayload, shouldParseAsJson = true): Observable<T> {
		return this.processRequest(method, payload, shouldParseAsJson).pipe(
			map((response) => {
				if (this.prefetchService.isPrefetchResponse(response)) {
					return response.data;
				}

				if (response.status < StatusCodes.Ok || response.status >= StatusCodes.MultipleChoices) {
					throw response;
				}

				try {
					return shouldParseAsJson && isString(response.body) ? JSON.parse(response.body) : response.body;
				} catch {
					return response.body;
				}
			})
		);
	}

	private processRequest(
		method: HttpRequestMethod,
		payload: RequestPayload,
		shouldParseAsJson: boolean
	): Observable<HttpResponse<string> | PrefetchResponse> {
		if (this.defaultConfiguration) {
			payload = merge<object, RequestPayload, RequestPayload>({}, this.defaultConfiguration, payload);
		}

		let body: any;
		if (payload.data) {
			body = shouldParseAsJson ? JSON.stringify(payload.data) : payload.data;
		}

		const httpCallerOriginalStackTrace = new Error().stack;
		const url = this.constructUrl(payload, this.defaultConfiguration && this.defaultConfiguration.url);
		const params = payload.searchParams ? this.createSearchParams(payload.searchParams) : undefined;

		// start prefetch hook
		const prefetchData = url && this.prefetchService.get(url);
		if (prefetchData) {
			return of(prefetchData);
		}
		// end prefetch hook
		const headers = new HttpHeaders(payload.headers);

		const responseType = payload.responseType ?? "text";

		this.registerDependency(method, url, params, headers);

		const start = Date.now();
		return this.http
			.request(method, url, {
				body,
				params,
				headers: payload.headers ? headers : undefined,
				observe: "response",
				responseType,
				withCredentials: payload.withCredentials,
			})
			.pipe(
				!globalThis.isDOMSSR ? timeout(globalThis.OBG_DOM_SSR_MAXIMUM_TIMEOUT ?? 300000) : tap(noop),
				tap((response) => {
					this.logger.info(
						"processRequest",
						`http call finished ${params?.keys().length ? `${url}?${params.toString()}` : url} at ${performance.now()}`,
						{
							url: params?.keys().length ? `${url}?${params.toString()}` : url,
							statusCode: response.status,
							durationInMs: Date.now() - start,
						}
					);
				}),
				catchError((error: HttpErrorResponse | TimeoutError) => {
					if (error instanceof TimeoutError) {
						return this.handleError(
							payload,
							new HttpErrorResponse({
								error: {
									message: `${url} timed out in ${globalThis.OBG_DOM_SSR_MAXIMUM_TIMEOUT ?? 300000} ms`,
									code: 408,
								},
								status: 408,
							}),
							httpCallerOriginalStackTrace,
							Date.now() - start
						);
					}
					return this.handleError(payload, error, httpCallerOriginalStackTrace, Date.now() - start);
				})
			);
	}

	private registerDependency(method: string, url: string, params: HttpParams | undefined, headers: HttpHeaders) {
		// Only GET requests will be registered
		// NOTE: Requests with credentials will not be handled correctly.
		if (!this.registerKeyDependency || method !== HttpRequestMethod.Get) {
			return;
		}

		const req = new HttpRequest(method, url, { params });
		const flatHeaders: Record<string, string> = {};
		for (const key of headers.keys()) {
			const val = headers.getAll(key);
			if (!val) {
				continue;
			}
			flatHeaders[key] = val.join(",");
		}

		this.registerKeyDependency(req.urlWithParams, flatHeaders);
	}

	private constructUrl(payload: RequestPayload, defaultUrl?: string): string {
		let urlTemplate = payload.url;
		if (!isExternalUrl(urlTemplate) && defaultUrl && payload.url !== defaultUrl) {
			urlTemplate = payload.url ? `${defaultUrl}/${payload.url}` : defaultUrl;
		}

		return urlTemplate && this.interpolateUrl(urlTemplate, payload.searchParams, payload.data);
	}

	private interpolateUrl(urlTemplate: string, search?: Record<string, any>, data?: any): string {
		const replaceMethod = (_match: string, paramNameFromUrl: string) => {
			const _findDeep = (label: string, options: any, removeFromObject = true) => {
				let value: string = options[label];

				// if it's not found in the object check
				// if the object has any nested objects
				if (isUndefined(value)) {
					for (const key in options) {
						if (!isObjectLike(options[key])) {
							continue;
						}
						value = _findDeep(paramNameFromUrl, options[key], removeFromObject);

						if (!isUndefined(value)) {
							break;
						}
					}
				} else if (removeFromObject) {
					// since we found the params remove them
					// from the object so that they do not get
					// sent twice
					delete options[label];
				}

				return value;
			};

			let foundValue = search && search[paramNameFromUrl];

			if (search && !isNil(foundValue)) {
				delete search[paramNameFromUrl];
			}

			// if we didn't find it in the params check in the data object
			if (isNil(foundValue) && data) {
				foundValue = _findDeep(paramNameFromUrl, data, false);
			}

			if (isArray(foundValue)) {
				throw new Error("url params should not contain any arrays");
			}

			return foundValue || "";
		};

		return urlTemplate.replace(/:([a-z]\w*)/gi, replaceMethod);
	}

	private handleError(
		payload: RequestPayload,
		res: HttpErrorResponse,
		httpCallerOriginalStackTrace: any,
		durationInMs: number
	) {
		if (res.status !== 0 && !some(payload.suppressErrorLogging, (err) => err === res.status)) {
			if (this.generateLogMessage(res) !== SITE_CORE_COOKIE_NOT_IN_HEADER) {
				this.logger.error("handleError", this.generateLogMessage(res), {
					requestPayload: payload,
					response: res,
					defaultConfiguration: this.defaultConfiguration,
					httpCallerOriginalStackTrace,
					durationInMs,
				});
			} else {
				this.logger.warn("handleWarning", this.generateLogMessage(res), {
					requestPayload: payload,
					response: res,
					defaultConfiguration: this.defaultConfiguration,
					httpCallerOriginalStackTrace,
					durationInMs,
				});
			}
		}
		return throwError(() => res);
	}

	private createSearchParams(params: Record<string, any>): HttpParams {
		let searchParams = new HttpParams();
		forEach(params, (value, key) => {
			if (!isNil(value)) {
				searchParams = searchParams.append(key, isDate(value) ? value.toISOString() : value);
			}
		});
		return searchParams;
	}

	private generateLogMessage(res: HttpErrorResponse): string {
		const fallBackError = "Server error";
		try {
			const errorResponse = JSON.parse(res.error) as ApiErrorResponse;

			if (!isEmpty(errorResponse.errors)) {
				return `${errorResponse.code}::${errorResponse.errors![0].code}`;
			}

			return errorResponse.code || `Empty Api Error Code::Status Text:${res.statusText}` || fallBackError;
		} catch (e) {
			return (
				`Failed to parse API Error Code::Status Text:${res.statusText} Status::${res.status} Error::${res.error}` ||
				fallBackError
			);
		}
	}
}
